import React, { useCallback, useMemo } from 'react';
import eventBus, { CUSTOM_EVENTS } from '../../../utils/EventBus';
import SvgIcon from '../../../utils/SvgIcon';
import DateControls from './DateControls';
import { useCalendarViewMode } from '../../hooks/useCalendarViewMode';
import { useDateLabelFormatter } from '../../hooks/useDateLabelFormatter';
import type { ApplyPendingFiltersCallback, CalendarDateFacet } from '../../../../types/search';

type CalendarTopControlsProps = Readonly<{
  applyPendingFilters?: ApplyPendingFiltersCallback;
  calendarDateFacet: CalendarDateFacet;
}>;

export default function CalendarTopControls({ applyPendingFilters, calendarDateFacet }: CalendarTopControlsProps) {
  const selectedValue = calendarDateFacet?.selectedValue || '';

  const handleDatePickerOpen = useCallback(() => {
    eventBus.emit(CUSTOM_EVENTS.PORTAL_OPEN_DATEPICKER_DRAWER);
  }, []);

  const mode = useCalendarViewMode(selectedValue);
  const formatDateLabel = useDateLabelFormatter(selectedValue);

  // Format the date label based on mode and selected value
  const formattedLabel = useMemo(() => {
    if (!selectedValue) {
      return 'Any';
    }

    if (mode === 'Custom') {
      const [dateFrom, dateTo] = selectedValue.split(',');
      if (dateFrom && dateTo) {
        return formatDateLabel(dateFrom, dateTo);
      }
    }

    // For Weekly/Monthly modes, format the date range
    const [dateFrom, dateTo] = selectedValue.split(',');
    if (dateFrom && dateTo) {
      return formatDateLabel(dateFrom, dateTo);
    }

    return 'Any';
  }, [selectedValue, mode, formatDateLabel]);

  return (
    <div>
      <p className="calendar-view__header-info">
        <SvgIcon iconName="calendar" className="calendar-view__header-icon" />
        <span className="calendar-view__header-label">{mode} view</span>
        <button onClick={handleDatePickerOpen} className="calendar-view__header-cta">
          Change dates
        </button>
      </p>
      {mode === 'Custom' ? <p className="date-controls__date">{formattedLabel}</p> : null}
      {mode !== 'Custom' && applyPendingFilters ? (
        <DateControls
          applyPendingFilters={applyPendingFilters}
          dateLabel={formattedLabel}
          selectedValue={selectedValue}
        />
      ) : null}
    </div>
  );
}
